/**
 * Dashboard Page
 *
 * Main dashboard displaying user's authorized applications.
 * Apps are browsed via category filter tabs and rendered in a unified flat grid.
 */

import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Command, Heart } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from '../contexts/AuthContext';
import {
  useMyApps,
  useMyCustomApps,
  useToggleHubAppFavorite,
} from "../hooks";
import { App } from "../types";
import {
  AppsLoadingSkeleton,
  DashboardHeader,
  EmptyAppsState,
  MyAppsSection,
} from "../components/dashboard";
import ErrorState from "../components/dashboard/ErrorState";
import SortableAppGrid from "../components/dashboard/SortableAppGrid";
import {
  readAppOrder,
  reorderVisibleApps,
  sortAppsByOrder,
  writeAppOrder,
} from "../utils/dashboardAppOrder";

// =============================================================================
// TYPES
// =============================================================================

type CategoryFilter = "all" | "favorites";

interface CategoryTab {
  id: CategoryFilter;
  label: string;
  count: number;
}

const DASHBOARD_CATEGORY_STORAGE_KEY = "dashboard:selectedCategory";

const parseStoredCategoryFilter = (value: string | null): CategoryFilter => {
  if (value === "favorites") return "favorites";
  return "all";
};

// =============================================================================
// DASHBOARD
// =============================================================================

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const { t } = useTranslation("dashboard");
  const [appOrder, setAppOrder] = useState<number[]>(() => {
    if (typeof window === "undefined" || userId == null) return [];
    return readAppOrder(userId);
  });
  const appOrderRef = useRef(appOrder);
  appOrderRef.current = appOrder;
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>(
    () => {
      if (typeof window === "undefined") return "all";
      return parseStoredCategoryFilter(
        localStorage.getItem(DASHBOARD_CATEGORY_STORAGE_KEY),
      );
    },
  );
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    sessionStorage.removeItem("appId");
  }, []);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_CATEGORY_STORAGE_KEY, selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (userId == null) return;
    const saved = readAppOrder(userId);
    appOrderRef.current = saved;
    setAppOrder(saved);
  }, [userId]);

  // Reset to 'all' when a search is started so results always show across all categories
  useEffect(() => {
    if (searchQuery) setSelectedCategory("all");
  }, [searchQuery]);

  const { data: apps = [], isLoading, isError, refetch } = useMyApps();
  const toggleHubFavorite = useToggleHubAppFavorite();
  const orderedApps = useMemo(
    () => sortAppsByOrder(apps, appOrder),
    [apps, appOrder],
  );

  // Category tabs: All + Favorites (if any)
  const categoryTabs = useMemo((): CategoryTab[] => {
    const tabs: CategoryTab[] = [
      { id: "all", label: "All", count: apps.length },
    ];
    const favCount = apps.filter((a) => a.isFavorite).length;
    if (favCount > 0) {
      tabs.push({ id: "favorites", label: "Favorites", count: favCount });
    }
    return tabs;
  }, [apps]);

  // Apps displayed in the main grid (filtered by tab + search)
  const displayedApps = useMemo((): App[] => {
    const base = selectedCategory === "favorites"
      ? orderedApps.filter((a) => a.isFavorite)
      : orderedApps;

    if (!searchQuery.trim()) return base;
    const q = searchQuery.toLowerCase().trim();
    return base.filter(
      (app) =>
        app.appName.toLowerCase().includes(q) ||
        app.appDescription?.toLowerCase().includes(q),
    );
  }, [selectedCategory, orderedApps, searchQuery]);

  const handleReorder = useCallback(
    (activeId: number, overId: number) => {
      if (userId == null) return;
      const ordered = sortAppsByOrder(apps, appOrderRef.current);
      const fullIds = ordered.map((app) => app.id);
      const visibleIds = (
        selectedCategory === "favorites"
          ? ordered.filter((app) => app.isFavorite)
          : ordered
      ).map((app) => app.id);
      const next = reorderVisibleApps(fullIds, visibleIds, activeId, overId);
      appOrderRef.current = next;
      setAppOrder(next);
      writeAppOrder(userId, next);
    },
    [apps, selectedCategory, userId],
  );

  // Total search results across all apps (for the results badge)
  const { data: customApps = [] } = useMyCustomApps();
  const totalSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return 0;
    const q = searchQuery.toLowerCase().trim();
    const hubMatches = apps.filter(
      (a) =>
        a.appName.toLowerCase().includes(q) ||
        a.appDescription?.toLowerCase().includes(q),
    ).length;
    const customMatches = customApps.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description && c.description.toLowerCase().includes(q)),
    ).length;
    return hubMatches + customMatches;
  }, [searchQuery, apps, customApps]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === "Escape" && searchQuery) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchQuery]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />

      <main className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 ">
        <motion.div layout className="flex flex-col">
          {/* ── Welcome + Search ── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <motion.section
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              aria-label={t("header.aria.welcome")}
            >
              <h1 className="text-2xl font-rubik font-bold text-haat-dark mb-0.5">
                {t("header.welcome", {
                  name:
                    user?.fullName?.split(" ")[0] || t("header.fallbackName"),
                })}
              </h1>
              <p className="text-sm text-haat-gray font-inter">
                {t("header.subtitle")}
              </p>
            </motion.section>

            {/* Search bar */}
            {!isLoading && apps.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="w-full sm:w-auto"
              >
                <div
                  className={`
                  w-full relative transition-all duration-300
                  sm:max-w-[18rem] sm:w-72
                  ${isSearchFocused ? "sm:max-w-[22rem] sm:w-88" : ""}
                `}
                >
                  <div
                    className={`
                    relative flex items-center bg-white rounded-xl border transition-all duration-300
                    ${
                      isSearchFocused
                        ? "border-gray-300"
                        : "border-gray-200 hover:border-gray-300"
                    }
                  `}
                  >
                    <Search
                      className={`absolute left-3 w-4 h-4 transition-colors duration-300 ${isSearchFocused ? "text-gray-500" : "text-gray-400"}`}
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      placeholder={t("search.placeholder")}
                      className="w-full py-2.5 pl-10 pr-20 bg-transparent text-gray-800 placeholder-gray-400 font-inter text-sm focus:outline-none rounded-xl"
                    />
                    <div className="absolute right-2 flex items-center gap-1.5">
                      {searchQuery && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          onClick={clearSearch}
                          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          <X size={14} />
                        </motion.button>
                      )}
                      {!searchQuery && (
                        <div className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400 text-[10px] font-medium">
                          <Command size={10} />
                          <span>K</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <AnimatePresence>
                    {searchQuery && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="absolute -bottom-6 left-0 right-0"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <p className="text-xs text-gray-500 text-center">
                          {totalSearchResults > 0 ? (
                            <Trans
                              ns="dashboard"
                              i18nKey="search.results"
                              count={totalSearchResults}
                              values={{ count: totalSearchResults }}
                              components={{
                                count: (
                                  <span className="font-semibold text-primary-500" />
                                ),
                              }}
                            />
                          ) : (
                            <span className="text-orange-500">
                              {t("search.noResults")}
                            </span>
                          )}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </div>

          {/* ── Main Applications heading ── */}
          <h2 className="text-sm font-semibold text-gray-700 mb-2">
            Main Applications
          </h2>

          {/* ── Category filter tabs ── */}
          {!isLoading && !isError && apps.length > 0 && !searchQuery && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="flex items-center gap-1 overflow-x-auto overflow-y-hidden mb-6 border-b border-gray-200 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              role="tablist"
              aria-label="Filter apps by category"
            >
              {categoryTabs.map((tab) => {
                const isSelected = selectedCategory === tab.id;
                return (
                  <button
                    key={String(tab.id)}
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => setSelectedCategory(tab.id)}
                    className={`
                    flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-150
                    ${
                      isSelected
                        ? "border-primary-500 text-primary-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                  >
                    {tab.id === "favorites" && (
                      <Heart
                        size={12}
                        className={
                          isSelected
                            ? "fill-primary-500 text-primary-500"
                            : "fill-gray-400 text-gray-400"
                        }
                      />
                    )}
                    {tab.label}
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                        isSelected
                          ? "bg-primary-100 text-primary-600"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* ── Main content ── */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <AppsLoadingSkeleton />
              </motion.div>
            ) : isError ? (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ErrorState
                  message={t("error.loadAppsMessage")}
                  onRetry={() => refetch()}
                />
              </motion.div>
            ) : apps.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <EmptyAppsState />
              </motion.div>
            ) : displayedApps.length === 0 ? (
              <motion.div
                key="no-results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {searchQuery ? (
                  <NoSearchResults query={searchQuery} onClear={clearSearch} />
                ) : (
                  <EmptyTabState
                    label={
                      categoryTabs.find((t) => t.id === selectedCategory)
                        ?.label ?? ""
                    }
                    onReset={() => setSelectedCategory("all")}
                  />
                )}
              </motion.div>
            ) : (
              <motion.div
                key={`grid-${selectedCategory}-${searchQuery ? "search" : "browse"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <SortableAppGrid
                  apps={displayedApps}
                  sortable={!searchQuery.trim()}
                  onReorder={handleReorder}
                  onToggleFavorite={(appId) => toggleHubFavorite.mutate(appId)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Custom Apps (user-defined external links) — always at the bottom ── */}
          <div className="mt-10">
            <MyAppsSection searchQuery={searchQuery} />
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface NoSearchResultsProps {
  query: string;
  onClear: () => void;
}

const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  query,
  onClear,
}) => {
  const { t } = useTranslation("dashboard");

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-5">
        <Search className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-rubik font-semibold text-gray-800 mb-2">
        {t("search.noApplicationsFoundTitle")}
      </h3>
      <p className="text-gray-500 mb-6 text-center max-w-md text-sm">
        <Trans
          ns="dashboard"
          i18nKey="search.noApplicationsFoundBody"
          values={{ query }}
          components={{ query: <span className="font-medium text-gray-700" /> }}
        />
        <br />
        {t("search.noApplicationsFoundHint")}
      </p>
      <button
        onClick={onClear}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
      >
        <X size={14} />
        {t("search.clear")}
      </button>
    </motion.div>
  );
};

interface EmptyTabStateProps {
  label: string;
  onReset: () => void;
}

const EmptyTabState: React.FC<EmptyTabStateProps> = ({ label, onReset }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-center justify-center py-20 text-center"
  >
    <p className="text-gray-500 text-sm mb-3">No apps in "{label}"</p>
    <button
      onClick={onReset}
      className="text-sm text-primary-600 hover:text-primary-700 font-medium underline-offset-2 hover:underline"
    >
      View all apps
    </button>
  </motion.div>
);

export default Dashboard;
